import React, { useContext, useMemo } from "react"
import { CurrentUserContext } from "../../../context/CurrentUserContext"
import { User } from "../../../types/common/user"

import DemographicsCard from "./DemographicsCard"
import QuarterYearSelector from "./QuarterYearSelector"
import QuarterCounterCard from "./QuarterCounterCard"
import BenefitsCard from "./BenefitsCard"
import InsuranceEligibilityCard from "./InsuranceEligibilityCard"
import QuarterTimelineCard from "./QuarterTimelineCard"
import BillableRequirementsSection from "./BillableRequirementsSection"
import ClientDocumentationSection from "./ClientDocumentationSection"
import ClinicalExcellenceSection from "./ClinicalExcellenceSection"
import QuarterlyPerformanceSection from "./QuarterlyPerformanceSection"
import SyncStatus from "./SyncStatus"
import ShoutoutCard from "./ShoutoutCard"
import SignOnTrackerCard from "./SignOnTrackerCard"
import NotificationDataCard from "./NotificationDataCard"
import NotificationHistoryCard from "./NotificationHistoryCard"
import GridCard from "./GridCard"
import CardSection from "./CardSection"
import DocumentComplianceSection from "./DocumentComplianceSection"

import { useDemographics } from "./hooks/useDemographics"
import { useProviderLayout } from "./hooks/useProviderLayout"
import { useRates } from "./hooks/useRates"
import { useQuarterState } from "./hooks/useQuarterState"
import { useMyProviderData } from "./hooks/useMyProviderData"
import { useBenefits } from "./hooks/useBenefits"
import { useInsuranceEligibility } from "./hooks/useInsuranceEligibility"
import { useQuarterTimeline } from "./hooks/useQuarterTimeline"
import { useEmployeeStatus } from "./hooks/useEmployeeStatus"
import { useHoursCompleted } from "./hooks/useHoursCompleted"
import { useSyncStatus } from "./hooks/useSyncStatus"

interface MyProviderDataProps {
  /** Render the data for this user instead of the signed-in user. Scoped to the caller;
   * it never changes the app-wide current user. */
  user?: User
  /** Hide the Employee Information block (status, role, salary, hire dates). The rest of
   * the page still renders. Used when a viewer may see another user's provider data but
   * not their employee details. */
  hideEmployeeInfo?: boolean
}

const MyProviderData: React.FC<MyProviderDataProps> = ({ user, hideEmployeeInfo }) => {
  const { currentUser } = useContext(CurrentUserContext)
  const subject = user ?? currentUser
  const { display: layoutDisplay } = useProviderLayout(subject)

  const display = useMemo(
    () => (hideEmployeeInfo ? { ...layoutDisplay, EmployeeInfo: false } : layoutDisplay),
    [layoutDisplay, hideEmployeeInfo]
  )

  const { year, quarter, timeline, years, calLoading, handleQuarterYearChange } = useQuarterState()

  // -----------------------------
  // User Info
  // -----------------------------
  const username = subject?.LoginName
  const state = subject?.State
  const role = subject?.Role
  const region = subject?.Region

  // -----------------------------
  // Data Hooks
  // -----------------------------
  const { data: checkpointData, loading: timelineLoading } =
    useQuarterTimeline(username, year, quarter, display.Timeline)

  const { employee, loading: employeeLoading } =
    useMyProviderData(username)

  const { status: employeeStatus } = useEmployeeStatus(username, year, quarter)

  const demographicsEnabled =
    !!username && !!year && !!quarter && (display.EmployeeInfo || display.TotalHoursRequired)

  const {
    data: demographicsData,
    finalization,
    loading: demographicsLoading,
  } = useDemographics(
    username,
    year ? parseInt(year) : undefined,
    quarter,
    state,
    demographicsEnabled
  )

  const { data: benefitsData, loading: benefitsLoading } =
    useBenefits(
      state,
      demographicsData?.tier,
      employee?.uhcEligibilityStatus,
      display.Benefits
    )

  const {
    headers: ratesHeader,
    rows: ratesData,
    loading: ratesLoading,
  } = useRates(
    username,
    role,
    demographicsData?.analystLevel,
    region,
    employeeStatus,
    display.Rates
  )

  const { data: insuranceData, loading: insuranceLoading } =
    useInsuranceEligibility(username, display.InsuranceEligibility)

  // when EPR (and the metrics) were last updated, the line under the quarter picker
  const { data: syncStatus } = useSyncStatus(username, year, quarter)

  const { pacePercentage } = useHoursCompleted(
    username, year, quarter, employeeStatus,
    !!username && !!year && display.Timeline
  )

  const pageLoading =
    calLoading ||
    demographicsLoading ||
    ratesLoading ||
    benefitsLoading ||
    employeeLoading ||
    insuranceLoading ||
    timelineLoading

  const notificationTimeline = useMemo(
    () =>
      timeline
        ? { QuarterStart: timeline.quarterStart, QuarterEnd: timeline.quarterEnd }
        : null,
    [timeline]
  )

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div className="grid grid-cols-12 gap-4 p-4 items-start overflow-visible">
      
      {/* Top Section */}
      <CardSection bordered={false}> 
        <div className="col-span-12 lg:col-span-4">
          <QuarterYearSelector
            year={year}
            quarter={quarter}
            years={years}
            onChange={handleQuarterYearChange}
          />
          {syncStatus && (
            <SyncStatus
              eprSyncedAt={syncStatus.eprSyncedAt}
              metricsCalculatedAt={syncStatus.calculatedAt}
              metricsBehindEpr={syncStatus.metricsBehindEpr}
              showMetrics={display.QuarterlyPerformance}
            />
          )}
        </div>

        {(display.EmployeeInfo || display.TotalHoursRequired) && (
          <div className="col-span-12 lg:col-span-4">
            <DemographicsCard
              title="Employee Information"
              data={demographicsData}
              loading={pageLoading}
              finalization={finalization}
              display={display}
            />
          </div>
        )}

        {timeline && (
          <div className="col-span-12 lg:col-span-4">
            <QuarterCounterCard
              title="Quarter Count"
              quarterStart={timeline.quarterStart}
              quarterEnd={timeline.quarterEnd}
              loading={pageLoading}
            />
          </div>
        )}
      </CardSection>

      {/* Rates / Benefits / Insurance */}
      <CardSection bordered={false}> 
        {display.Rates && (
          <div className="col-span-12 lg:col-span-4">
            <GridCard
              title="Rates"
              headers={ratesHeader}
              data={ratesData ? [ratesData] : []}
              loading={pageLoading}
            />
          </div>
        )}

        {display.Benefits && (
          <div className="col-span-12 lg:col-span-4">
            <BenefitsCard
              title="Benefits Information"
              data={{
                status: employeeStatus,
                role,
                benefitsInfo: benefitsData,
              }}
              loading={pageLoading}
            />
          </div>
        )}

        {display.InsuranceEligibility && (
          <div className="col-span-12 lg:col-span-4">
            <InsuranceEligibilityCard
              title="Health Insurance Eligibility"
              data={insuranceData}
              loading={pageLoading}
            />
          </div>
        )}
      </CardSection>

      {/* Timeline */}
      {display.Timeline && (
        <div className="col-span-12">
          <QuarterTimelineCard
            title="Quarterly Timeline"
            timeline={timeline}
            checkpoint={checkpointData}
            loading={pageLoading}
            pacePercentage={pacePercentage}
          />
        </div>
      )}

      <QuarterlyPerformanceSection
        username={username}
        year={year}
        quarter={quarter}
        display={display}
        showSyncStatus={false}
      />

      {/* Remaining Sections */}
      <BillableRequirementsSection
        username={username}
        year={year}
        quarter={quarter}
        status={employeeStatus}
        display={display}
      />

      <DocumentComplianceSection
        username={username}
        display={display}
      />

      <ClinicalExcellenceSection
        username={username}
        display={display}
      />

      <SignOnTrackerCard
        username={username}
        display={display}
      />

      <ShoutoutCard
        username={username}
        year={year}
        quarter={quarter}
        display={display}
      />

      <ClientDocumentationSection
        username={username}
        year={year}
        quarter={quarter}
        display={display}
      />

      <NotificationDataCard
        username={username}
        year={year}
        quarter={quarter}
        display={display}
      />

      {notificationTimeline && (
        <NotificationHistoryCard
          username={username}
          timeline={notificationTimeline}
          display={display}
        />
      )}
    </div>
  )
}

export default MyProviderData;